import React, { useCallback, useEffect, useState } from "react";

import { DeprecatedUISharedButton } from "packages/ui-shared/components/Button/Button";
import { Accordion } from "packages/ui-shared/components/Accordion/Accordion";
import type { MembershipOptionsV2 } from "@shared/graphql.types";

import * as styles from "./MembershipOptionsModal.css";
import { Logger } from "lib/logger";
import {
  PAGE_ELEMENT_CLICKED_EVENT_KEY,
  PAGE_ELEMENT_VIEWED_EVENT_KEY
} from "tracking/hooks/usePageElementEvents";
import { dispatch } from "@shared/utils.events";
import { Drawer, MarkdownRenderer } from "@shared/ui.primitives";

export type MembershipOptionsModalProps = {
  isOpen?: boolean;
  onClose: () => void;
  onSelect: (membership: MembershipOptionsV2) => void;
  initialMembershipSku: string;
  memberships: MembershipOptionsV2[] | undefined;
  defaultMembershipSku?: string;
};

const MEMBERSHIP_OPTIONS_RADIO_NAME = "membership-options";

export const MembershipOptionsModal: React.FC<MembershipOptionsModalProps> = ({
  onClose,
  isOpen = false,
  onSelect,
  memberships,
  defaultMembershipSku,
  initialMembershipSku
}) => {
  const [selectedValue, setSelectedValue] = useState(initialMembershipSku);
  const [loading, setLoading] = useState(false);

  const handleSelectMembership = async () => {
    setLoading(true);
    const selectedMembership = memberships?.find(
      (membership) => membership.sku === selectedValue
    );

    if (selectedMembership) {
      try {
        await onSelect(selectedMembership);
        setLoading(false);
        dispatch(PAGE_ELEMENT_CLICKED_EVENT_KEY, {
          target: "og_membership_selection_panel",
          target2: defaultMembershipSku,
          location: "og_membership_selection_panel",
          locationDetails: defaultMembershipSku,
          details: "og_add_membership_to_bag",
          details2: selectedMembership.sku,
          details3: selectedMembership.name
        });
      } catch (err: unknown) {
        Logger.warn(`Membership selection failed, due to error: "${err}"`);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    isOpen &&
      defaultMembershipSku &&
      dispatch(PAGE_ELEMENT_VIEWED_EVENT_KEY, {
        target: "og_membership_selection_panel",
        target2: defaultMembershipSku,
        location: "og_membership_selection_panel",
        locationDetails: defaultMembershipSku
      });
  }, [isOpen, defaultMembershipSku]);

  const onAccordionChange = useCallback(
    (value: string) => {
      const selectedMembership = memberships?.find(
        (membership) => membership.sku === value
      );

      dispatch(PAGE_ELEMENT_CLICKED_EVENT_KEY, {
        target: "og_membership_selection_panel",
        target2: defaultMembershipSku,
        location: "og_membership_selection_panel",
        locationDetails: defaultMembershipSku,
        details: "og_select_membership",
        details2: selectedMembership?.sku,
        details3: selectedMembership?.name
      });
      setSelectedValue(value);
    },
    [defaultMembershipSku, memberships]
  );

  const handleModalDismiss = useCallback(() => {
    dispatch(PAGE_ELEMENT_CLICKED_EVENT_KEY, {
      target: "og_membership_selection_panel",
      target2: defaultMembershipSku,
      location: "og_membership_selection_panel",
      locationDetails: defaultMembershipSku,
      details: "og_close"
    });
    onClose();
  }, [defaultMembershipSku, onClose]);

  return (
    <Drawer
      isOpen={isOpen}
      handleDismiss={handleModalDismiss}
      ariaDescription="See the details of the membership options available"
      ariaLabel="Membership Options"
      id="membership-options-drawer"
      dataTestId="membership-options-drawer"
      headerId="membership-options-drawer-header"
    >
      <Drawer.Header
        handleDismiss={handleModalDismiss}
        id="membership-options-drawer-header"
      >
        Membership options
      </Drawer.Header>
      <Drawer.Body>
        <Accordion
          expandAll={true}
          onChange={onAccordionChange}
          selectedValue={selectedValue}
          radioName={MEMBERSHIP_OPTIONS_RADIO_NAME}
        >
          {memberships?.map((membership) => (
            <Accordion.Item
              value={membership.sku}
              key={membership.sku}
              radioName={MEMBERSHIP_OPTIONS_RADIO_NAME}
            >
              <Accordion.Item.Label
                radioName={MEMBERSHIP_OPTIONS_RADIO_NAME}
                header={membership.name}
                rightContent={membership.priceExplainer}
              />
              <Accordion.Item.Content>
                <MarkdownRenderer
                  content={membership.perks}
                  className={styles.bodyContent}
                />
              </Accordion.Item.Content>
            </Accordion.Item>
          ))}
        </Accordion>
      </Drawer.Body>
      <Drawer.Footer>
        <DeprecatedUISharedButton
          fullWidth
          variant="primary"
          loading={loading}
          handleClick={handleSelectMembership}
        >
          Add Product And Join Beauty Pie+
        </DeprecatedUISharedButton>
      </Drawer.Footer>
    </Drawer>
  );
};
